// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import MaterialButton, {
  ButtonProps as MaterialButtonProps,
} from "@material-ui/core/Button";
import { ROUTE_DEFINITIONS, getPath } from "routes/routes";
import React, { FunctionComponent, createRef, useCallback } from "react";
import { Theme, createStyles, makeStyles } from "@material-ui/core/styles";

import { ButtonImplOverride } from "config/ButtonOverrides";
import ExternalLink from "./ExternalLink";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

const style = makeStyles((theme: Theme) =>
  createStyles({
    nextButton: {
      minWidth: "185px",
      margin: "30px 0px",
    },
  })
);

const ButtonImpl: FunctionComponent<MaterialButtonProps> =
  ButtonImplOverride ||
  ((props: MaterialButtonProps) => (
    <MaterialButton variant="contained" {...props}>
      {props.children}
    </MaterialButton>
  ));

export const Button: FunctionComponent<MaterialButtonProps> = props => {
  const { form, onClick } = props;
  const ref = createRef<HTMLButtonElement>();

  const onClickWrapper = useCallback(
    e => {
      if (form && !ref.current?.form) {
        // IE11 doesn't support the `form` attribute on buttons, trigger the form submission manually
        const formElement = document.getElementById(form) as HTMLFormElement;
        if (formElement) {
          const event = document.createEvent("Event");
          event.initEvent("submit", false, false);
          formElement.dispatchEvent(event);
        }
      }
      onClick && onClick(e);
    },
    [onClick, form, ref]
  );

  return (
    <ButtonImpl
      color="primary"
      {...props}
      onClick={onClickWrapper}
      buttonRef={ref}
    >
      {props.children}
    </ButtonImpl>
  );
};

export interface NavButtonProps extends MaterialButtonProps {
  route: string;
  text: string;
}

export const NavButton: FunctionComponent<NavButtonProps> = props => {
  const styles = style();
  const { route, text } = props;

  if (route.startsWith("http")) {
    return (
      <ExternalLink href={route}>
        <Button className={styles.nextButton} size="large" {...props}>
          <span>{text}</span>
          <OpenInNewIcon fontSize="inherit" />
        </Button>
      </ExternalLink>
    );
  } else {
    return (
      <Link to={getPath(ROUTE_DEFINITIONS[props.route])}>
        <Button className={styles.nextButton} size="large" {...props}>
          <span>{text}</span>
        </Button>
      </Link>
    );
  }
};

export interface ContinueButtonProps extends MaterialButtonProps {
  text?: string;
}

export const ContinueButton: FunctionComponent<ContinueButtonProps> = props => {
  const styles = style();

  return (
    <Button className={styles.nextButton} size="large" {...props}>
      <span>{props.text ? props.text : "Continue"}</span>
      <span className="icon is-medium">
        <FontAwesomeIcon icon="arrow-right" />
      </span>
    </Button>
  );
};
